import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { Window, Dialog, ConfirmDialog, EmptyState, Loading } from "../../components/VB";
import { toast } from "sonner";
import { Plus, Trash2, Wallet, CheckCircle2, Settings as SettingsIcon, Palette, Link2, Unlink } from "lucide-react";

// Mirror PaymentDeveloper.jsx (Rekap & Bayar + Pengaturan Rate) — bedanya HANYA satu rate
// ("Rate Per Design", per Type Web), tidak ada padanan Rate Domain karena desainer tidak
// dibayar berdasarkan ekstensi domain.
const idr = (n) => "Rp " + Math.round(parseFloat(n || 0)).toLocaleString("id-ID");

// Sama seperti getCurrentPeriode() di ProjectPayment.jsx — tanggal 1-15 default ke periode "15",
// 16-akhir bulan default ke periode "30".
const getCurrentPeriode = () => {
  const today = new Date().getDate();
  return today <= 15 ? "15" : "30";
};

export default function PaymentDesigner() {
  const [tab, setTab] = useState("rekap"); // rekap, settings
  const [recap, setRecap] = useState([]);
  const [designers, setDesigners] = useState([]);
  const [types, setTypes] = useState([]);
  const [designRates, setDesignRates] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);

  const today = new Date().toISOString().slice(0, 10);
  const [payTarget, setPayTarget] = useState(null);
  const [payForm, setPayForm] = useState({ payment_date: today, amount_paid: 0, metode: "Transfer", catatan: "" });
  const [detail, setDetail] = useState(null);

  // Filter periode Rekap & Bayar — SAMA seperti filter periode di Payment Website (ProjectPayment.jsx):
  // periode "15" (due 1-15) / "30" (due 16-30/31) / "all" (semua), digabung filter bulan (YYYY-MM).
  const [filterBulan, setFilterBulan] = useState(today.slice(0, 7));
  const [filterPeriode, setFilterPeriode] = useState(getCurrentPeriode);

  const load = async () => {
    setLoading(true);
    try {
      const [r, d, t, dr, emp] = await Promise.all([
        api.get("/designer-payment/recap", { params: { bulan: filterBulan, periode: filterPeriode } }),
        api.get("/designers"), api.get("/web-types"),
        api.get("/designer-rates"), api.get("/employees"),
      ]);
      setRecap(r.data); setDesigners(d.data); setTypes(t.data);
      setDesignRates(dr.data); setEmployees(emp.data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterBulan, filterPeriode]);

  const submitPay = async () => {
    try {
      await api.post("/designer-payment", {
        designer_id: payTarget.designer_id, periode: payTarget.periode,
        payment_date: payForm.payment_date, amount_paid: parseFloat(payForm.amount_paid || 0),
        metode: payForm.metode, catatan: payForm.catatan,
      });
      toast.success("Pembayaran designer tercatat (arus kas keluar otomatis)");
      setPayTarget(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const totalEarning = recap.reduce((s, r) => s + (r.total_earning_idr || 0), 0);
  const totalPaid = recap.reduce((s, r) => s + (r.paid_idr || 0), 0);
  const totalSisa = recap.reduce((s, r) => s + (r.sisa_idr || 0), 0);

  return (
    <Window
      title="Payment Designer (IDR)"
      actions={
        <>
          <button className={`vb-btn ${tab === "rekap" ? "vb-btn-primary" : ""}`} onClick={() => setTab("rekap")} data-testid="tab-designer-rekap">Rekap & Bayar</button>
          <button className={`vb-btn ${tab === "settings" ? "vb-btn-primary" : ""}`} onClick={() => setTab("settings")} data-testid="tab-designer-settings"><SettingsIcon className="w-3 h-3 inline mr-1" />Pengaturan Rate</button>
        </>
      }
    >
      {tab === "rekap" && (
        <>
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8, flexWrap: "wrap" }}>
            <select
              className="vb-select"
              style={{ width: 140 }}
              value={filterPeriode}
              onChange={(e) => setFilterPeriode(e.target.value)}
              data-testid="designer-filter-periode"
            >
              <option value="15">Periode 1-15</option>
              <option value="30">Periode 16-30/31</option>
              <option value="all">Semua Periode</option>
            </select>
            <input
              type="month"
              className="vb-input"
              style={{ width: 160 }}
              value={filterBulan}
              onChange={(e) => setFilterBulan(e.target.value)}
              data-testid="designer-filter-bulan"
              title="Filter bulan berdasarkan Due Date project (sama seperti Payment Website) — tetap berlaku walau periode dipilih 'Semua Periode'"
            />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 8, marginBottom: 12 }}>
            <div className="vb-statcard"><div className="vb-statcard-label">Total Earning Designer</div><div className="vb-statcard-value" style={{ color: "#003399", fontSize: 16 }}>{idr(totalEarning)}</div></div>
            <div className="vb-statcard"><div className="vb-statcard-label">Sudah Dibayar</div><div className="vb-statcard-value" style={{ color: "#4f8a10", fontSize: 16 }}>{idr(totalPaid)}</div></div>
            <div className="vb-statcard"><div className="vb-statcard-label">Belum Dibayar</div><div className="vb-statcard-value" style={{ color: "#d8000c", fontSize: 16 }}>{idr(totalSisa)}</div></div>
          </div>

          {loading ? <Loading /> : recap.length === 0 ? <EmptyState message="Belum ada project terhubung designer pada periode/bulan ini. Coba ganti filter periode, atau tambahkan/pilih Designer pada menu Payment Website lalu set rate designer di tab Pengaturan Rate." /> : (
            <div style={{ overflowX: "auto" }}>
              <table className="vb-table">
                <thead>
                  <tr>
                    <th>Periode (Due 15/30)</th>
                    <th>Designer</th>
                    <th style={{ textAlign: "center" }}>Jml Project</th>
                    <th style={{ textAlign: "right" }}>Total Earning</th>
                    <th style={{ textAlign: "right" }}>Dibayar</th>
                    <th style={{ textAlign: "right" }}>Sisa</th>
                    <th>Status</th>
                    <th>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {recap.map((r) => (
                    <tr key={`${r.designer_id}|${r.periode}`}>
                      <td style={{ fontWeight: 700, color: "#003399" }}>{r.periode}</td>
                      <td><strong>{r.designer_nama}</strong></td>
                      <td style={{ textAlign: "center" }}>
                        <button className="vb-btn" style={{ padding: "1px 6px" }} onClick={() => setDetail(r)} data-testid={`designer-detail-${r.designer_id}-${r.periode}`}>{r.count} ▾</button>
                      </td>
                      <td style={{ textAlign: "right" }}>{idr(r.total_earning_idr)}</td>
                      <td style={{ textAlign: "right", color: "#4f8a10" }}>{idr(r.paid_idr)}</td>
                      <td style={{ textAlign: "right", color: "#d8000c", fontWeight: 600 }}>{idr(r.sisa_idr)}</td>
                      <td>
                        {r.status === "Lunas" ? <span className="vb-badge vb-badge-approved"><CheckCircle2 className="w-3 h-3 inline mr-1" />Lunas</span>
                          : r.status === "Sebagian" ? <span className="vb-badge vb-badge-pending">Sebagian</span>
                          : <span className="vb-badge vb-badge-rejected">Pending</span>}
                      </td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        {r.status !== "Lunas" && (
                          <button className="vb-btn-primary" style={{ padding: "2px 6px" }} onClick={() => { setPayTarget(r); setPayForm({ payment_date: today, amount_paid: r.sisa_idr, metode: "Transfer", catatan: `Pembayaran periode ${r.periode}` }); }} data-testid={`designer-pay-${r.designer_id}-${r.periode}`}>
                            <Wallet className="w-3 h-3 inline mr-1" />Bayar
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {tab === "settings" && (
        <>
          <DesignerEmployeeLinkCard designers={designers} employees={employees} reload={load} />
          <RateSettings designers={designers} types={types} designRates={designRates} reload={load} />
        </>
      )}

      {/* Payment Dialog */}
      <Dialog open={!!payTarget} onClose={() => setPayTarget(null)} title="Bayar Designer" width={460}
        footer={<><button className="vb-btn" onClick={() => setPayTarget(null)}>Batal</button><button className="vb-btn-primary" onClick={submitPay} data-testid="designer-pay-save">Bayarkan</button></>}>
        {payTarget && (
          <>
            <div style={{ background: "#FFFFE1", border: "1px solid #828790", padding: 8, fontSize: 12, marginBottom: 12 }}>
              <strong>{payTarget.designer_nama}</strong> · Periode {payTarget.periode}<br />
              Total: {idr(payTarget.total_earning_idr)} &middot; Dibayar: {idr(payTarget.paid_idr)} &middot; <strong style={{ color: "#d8000c" }}>Sisa: {idr(payTarget.sisa_idr)}</strong>
            </div>
            <div className="vb-form-row"><label className="vb-label">Tanggal Bayar*</label>
              <input type="date" className="vb-input" value={payForm.payment_date} onChange={(e) => setPayForm({ ...payForm, payment_date: e.target.value })} /></div>
            <div className="vb-form-row"><label className="vb-label">Jumlah (IDR)*</label>
              <input type="number" className="vb-input" value={payForm.amount_paid} onChange={(e) => setPayForm({ ...payForm, amount_paid: e.target.value })} data-testid="designer-pay-amount" /></div>
            <div className="vb-form-row"><label className="vb-label">Metode</label>
              <select className="vb-select" value={payForm.metode} onChange={(e) => setPayForm({ ...payForm, metode: e.target.value })}>
                <option>Transfer</option><option>Cash</option>
              </select></div>
            <div className="vb-form-row"><label className="vb-label">Catatan</label>
              <textarea rows={2} className="vb-textarea" value={payForm.catatan} onChange={(e) => setPayForm({ ...payForm, catatan: e.target.value })} /></div>
          </>
        )}
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={!!detail} onClose={() => setDetail(null)} title={`Detail Project — ${detail?.designer_nama || ""} (${detail?.periode || ""})`} width={560}
        footer={<button className="vb-btn" onClick={() => setDetail(null)}>Tutup</button>}>
        {detail && (
          <table className="vb-table">
            <thead><tr><th>Project</th><th>Domain</th><th>Type</th><th style={{ textAlign: "right" }}>Rate Design</th><th style={{ textAlign: "right" }}>Earning</th></tr></thead>
            <tbody>
              {detail.projects.map((p, i) => (
                <tr key={i}>
                  <td><strong>{p.nama_project}</strong></td>
                  <td style={{ fontFamily: "Consolas, monospace", fontSize: 11 }}>{p.domain}</td>
                  <td>{p.type_web}</td>
                  <td style={{ textAlign: "right" }}>{idr(p.rate_design_idr)}</td>
                  <td style={{ textAlign: "right", fontWeight: 700, color: "#003399" }}>{idr(p.earning_idr)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Dialog>
    </Window>
  );
}

// Delegasi ke akun KARYAWAN yang sudah ada — mirror DeveloperEmployeeLinkCard di
// PaymentDeveloper.jsx persis, cuma nama field/endpoint designer_id/`/designers/{id}`.
function DesignerEmployeeLinkCard({ designers, employees, reload }) {
  const [linkTarget, setLinkTarget] = useState(null);
  const [empId, setEmpId] = useState("");
  const empName = (id) => employees.find((e) => e.id === id)?.nama_lengkap || "-";

  const doSave = async () => {
    try {
      await api.put(`/designers/${linkTarget.id}`, {
        nama: linkTarget.nama, kontak: linkTarget.kontak || "", email: linkTarget.email || "",
        employee_id: empId || null,
      });
      toast.success(empId ? `Designer "${linkTarget.nama}" terhubung ke karyawan ${empName(empId)}.` : `Tautan karyawan untuk "${linkTarget.nama}" dilepas.`);
      setLinkTarget(null); reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
        <Link2 className="w-3.5 h-3.5" style={{ color: "#003399" }} />
        <h3 className="vb-h1" style={{ margin: 0 }}>Delegasi ke Karyawan</h3>
      </div>
      <div style={{ fontSize: 11, color: "#666", marginBottom: 8 }}>
        Hubungkan designer ke akun karyawan yang sudah ada — karyawan tersebut otomatis dapat menu "Pendapatan → List Designer Project" di dashboard karyawan-nya sendiri (login tetap sebagai karyawan biasa, tidak ada akun terpisah).
      </div>
      {designers.length === 0 ? <EmptyState message="Belum ada designer. Tambahkan dulu di Payment Website → Pengaturan → Designer." /> : (
        <table className="vb-table">
          <thead><tr><th>Designer</th><th>Karyawan Terhubung</th><th>Aksi</th></tr></thead>
          <tbody>
            {designers.map((d) => (
              <tr key={d.id}>
                <td><strong>{d.nama}</strong></td>
                <td>{d.employee_nama ? <span className="vb-badge vb-badge-approved">{d.employee_nama}</span> : <span className="vb-badge vb-badge-rejected">Belum</span>}</td>
                <td>
                  <button className="vb-btn" style={{ padding: "2px 6px" }} onClick={() => { setLinkTarget(d); setEmpId(d.employee_id || ""); }} data-testid={`designer-link-${d.id}`}>
                    {d.employee_id ? <><Link2 className="w-3 h-3 inline mr-1" />Ubah</> : <><Link2 className="w-3 h-3 inline mr-1" />Hubungkan</>}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <Dialog open={!!linkTarget} onClose={() => setLinkTarget(null)} title={`Hubungkan Designer ke Karyawan: ${linkTarget?.nama || ""}`} width={460}
        footer={<><button className="vb-btn" onClick={() => setLinkTarget(null)}>Batal</button><button className="vb-btn-primary" onClick={doSave} data-testid="designer-link-save">Simpan</button></>}>
        <div className="vb-form-row"><label className="vb-label">Karyawan</label>
          <select className="vb-select" value={empId} onChange={(e) => setEmpId(e.target.value)} data-testid="designer-link-employee">
            <option value="">- tidak terhubung -</option>
            {employees.map((e) => <option key={e.id} value={e.id}>{e.nama_lengkap}</option>)}
          </select>
        </div>
        {empId && (
          <div style={{ fontSize: 11, color: "#666" }}>
            <Unlink className="w-3 h-3 inline mr-1" />Pilih "- tidak terhubung -" untuk melepas tautan (menu Pendapatan karyawan ybs akan hilang lagi).
          </div>
        )}
      </Dialog>
    </div>
  );
}

// Hanya SATU section rate ("Rate Per Design", per Type Web) — beda dari RateSettings milik
// PaymentDeveloper.jsx yang punya 2 sub-tab (Web + Domain), makanya tidak perlu switcher tab
// di sini, langsung tampilkan RateTable-nya.
function RateSettings({ designers, types, designRates, reload }) {
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
        <Palette className="w-3.5 h-3.5" style={{ color: "#003399" }} />
        <h3 className="vb-h1" style={{ margin: 0 }}>Rate Per Design (per Type Web)</h3>
      </div>
      <RateTable endpoint="/designer-rates" items={designRates} reload={reload} designers={designers}
        optionKey="type_web" optionLabel="Type Web" options={types.map((t) => t.nama)} />
    </div>
  );
}

function RateTable({ endpoint, items, reload, designers, optionKey, optionLabel, options }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ designer_id: "", [optionKey]: "", rate_idr: 0 });
  const [del, setDel] = useState(null);
  const designerName = (id) => designers.find((d) => d.id === id)?.nama || "-";

  const submit = async () => {
    try {
      await api.post(endpoint, { designer_id: form.designer_id, [optionKey]: form[optionKey], rate_idr: parseFloat(form.rate_idr || 0) });
      toast.success("Rate tersimpan"); setOpen(false); reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const doDelete = async () => {
    try { await api.delete(`${endpoint}/${del.id}`); toast.success("Dihapus"); setDel(null); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
        <h3 className="vb-h1" style={{ margin: 0 }}>Rate {optionLabel} per Designer (IDR)</h3>
        <button className="vb-btn-primary" onClick={() => { setForm({ designer_id: "", [optionKey]: "", rate_idr: 0 }); setOpen(true); }} data-testid={`add-rate-${optionKey}`}><Plus className="w-3 h-3 inline mr-1" />Tambah / Update Rate</button>
      </div>
      {items.length === 0 ? <EmptyState message="Belum ada rate. Set rate agar earning designer terhitung." /> : (
        <table className="vb-table">
          <thead><tr><th>Designer</th><th>{optionLabel}</th><th style={{ textAlign: "right" }}>Rate (IDR)</th><th>Aksi</th></tr></thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id}>
                <td><strong>{designerName(it.designer_id)}</strong></td>
                <td>{it[optionKey]}</td>
                <td style={{ textAlign: "right", fontWeight: 600, color: "#003399" }}>{idr(it.rate_idr)}</td>
                <td><button className="vb-btn-danger" style={{ padding: "2px 6px" }} onClick={() => setDel(it)}><Trash2 className="w-3 h-3" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} title={`Set Rate ${optionLabel}`}
        footer={<><button className="vb-btn" onClick={() => setOpen(false)}>Batal</button><button className="vb-btn-primary" onClick={submit} disabled={!form.designer_id || !form[optionKey]} data-testid="rate-save">Simpan</button></>}>
        <div className="vb-form-row"><label className="vb-label">Designer*</label>
          <select className="vb-select" value={form.designer_id} onChange={(e) => setForm({ ...form, designer_id: e.target.value })} data-testid="rate-designer">
            <option value="">- pilih designer -</option>
            {designers.map((d) => <option key={d.id} value={d.id}>{d.nama}</option>)}
          </select></div>
        <div className="vb-form-row"><label className="vb-label">{optionLabel}*</label>
          <select className="vb-select" value={form[optionKey]} onChange={(e) => setForm({ ...form, [optionKey]: e.target.value })} data-testid="rate-option">
            <option value="">- pilih -</option>
            {options.map((o) => <option key={o} value={o}>{o}</option>)}
          </select></div>
        <div className="vb-form-row"><label className="vb-label">Rate (IDR)*</label>
          <input type="number" className="vb-input" value={form.rate_idr} onChange={(e) => setForm({ ...form, rate_idr: e.target.value })} data-testid="rate-idr" /></div>
      </Dialog>

      <ConfirmDialog open={!!del} onClose={() => setDel(null)} onConfirm={doDelete} message="Hapus rate ini?" />
    </div>
  );
}
